import "./compliance.css";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { DatePicker, Drawer, Input, InputNumber, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faUsers } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getDocumentConfigQuery } from "@src/util/query/documentConfig.query";
import { finalizeDocumentUploadMutation, newComplianceDocumentMutation, setDocumentAcknowledgementMutation } from "@src/util/mutation/compliance.mutation";
import { AudiencePickerModal } from "./audiencePicker.modal";
import { describeAssignment, emptyAudience, hasAudience, summariseAudience, uploadThenFinalize } from "./complianceHelpers";

/**
 * Company-wide compliance documents (SOPs, policies, generic risk assessments...): upload
 * once, choose who must acknowledge it. Also edits an existing one's details/audience.
 * Site-specific documents are uploaded from the site's Documents tab instead.
 */
export const ComplianceDocumentDrawer = ({ visible, onClose, onSaved = () => {}, editDoc = null }) => {
    const dispatch = useDispatch();
    const fileRef = useRef();
    const editing = !!editDoc;

    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [documentConfigId, setDocumentConfigId] = useState(null);
    const [description, setDescription] = useState("");
    const [audience, setAudience] = useState(emptyAudience());
    const [audienceOpen, setAudienceOpen] = useState(false);
    const [method, setMethod] = useState("signature");
    const [dueInDays, setDueInDays] = useState(7);
    const [reviewDate, setReviewDate] = useState("");
    const [expiryDate, setExpiryDate] = useState("");
    const [saving, setSaving] = useState(false);

    const { data: configData } = useQuery(getDocumentConfigQuery, {
        variables: { objectType: "Compliance", isArchived: false },
        skip: !visible,
        fetchPolicy: "cache-and-network",
    });
    const types = configData?.getDocumentConfigByObject?.response || [];

    const [newComplianceDocument] = useMutation(newComplianceDocumentMutation);
    const [finalizeUpload] = useMutation(finalizeDocumentUploadMutation);
    const [setAcknowledgement] = useMutation(setDocumentAcknowledgementMutation);

    useEffect(() => {
        if (!visible) return;
        setFile(null);
        setTitle(editDoc?.title || "");
        setDocumentConfigId(null);
        setDescription(editDoc?.description || "");
        setAudience({ ...emptyAudience(), ...(editDoc?.audience || {}) });
        setMethod(editDoc?.acknowledgementMethod || "signature");
        setDueInDays(editDoc?.dueInDays ?? 7);
        setReviewDate(editDoc?.reviewDate || "");
        setExpiryDate("");
    }, [visible, editDoc]);

    // picking a document type applies its defaults (Settings > Document Checklist > Compliance)
    const chooseType = (id) => {
        setDocumentConfigId(id || null);
        const type = types.find((t) => t._id === id);
        if (!type) return;
        if (!title) setTitle(type.name);
        if (type.acknowledgementMethod) setMethod(type.acknowledgementMethod);
        if (type.dueInDays !== undefined && type.dueInDays !== null) setDueInDays(type.dueInDays);
        if (type.reviewEveryMonths) setReviewDate(dayjs().add(type.reviewEveryMonths, "month").format("YYYY-MM-DD"));
    };

    const missing = [];
    if (!editing && !file) missing.push("a file");
    if (!title.trim()) missing.push("a title");
    if (!hasAudience(audience)) missing.push("who must acknowledge it");

    const save = async () => {
        if (missing.length) return;
        setSaving(true);
        try {
            if (editing) {
                const res = await setAcknowledgement({
                    variables: {
                        documentId: editDoc._id,
                        input: { title: title.trim(), description, audience, acknowledgementMethod: method, dueInDays, reviewDate: reviewDate || "" },
                    },
                });
                const result = res?.data?.setDocumentAcknowledgement?.response;
                const changed = (result?.assigned || 0) + (result?.reopened || 0) + (result?.revoked || 0);
                dispatch(setNotification({ error: false, notificationState: true, message: changed ? `Saved - ${describeAssignment(result).toLowerCase()}` : "Saved" }));
            } else {
                const res = await newComplianceDocument({
                    variables: {
                        input: {
                            fileName: file.name,
                            title: title.trim(),
                            description,
                            documentConfigId,
                            audience,
                            requiresAcknowledgement: true,
                            acknowledgementMethod: method,
                            dueInDays,
                            reviewDate: reviewDate || "",
                            expiryDate: expiryDate || "",
                        },
                    },
                });
                const { url, documentId } = res?.data?.newComplianceDocument?.response || {};
                const result = await uploadThenFinalize({ url, documentId, file, finalize: finalizeUpload });
                dispatch(setNotification({ error: false, notificationState: true, message: `${title.trim()} uploaded - ${describeAssignment(result).toLowerCase()}` }));
            }
            onSaved();
            onClose();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            title={editing ? `Edit ${editDoc?.title || "document"}` : "New company-wide document"}
            placement="right"
            open={visible}
            onClose={saving ? undefined : onClose}
            width={600}
            maskClosable={false}
            closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon" />}
            footer={
                <div className="drawer-footer" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button className={saving || missing.length ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={saving || missing.length > 0} onClick={save}>
                        {saving ? <Spinner /> : editing ? "Save" : "Upload and send"}
                    </button>
                    <button className="drawer-outlined-btn" disabled={saving} onClick={onClose}>Cancel</button>
                    {missing.length > 0 && <span className="cmp-hint">Add {missing.join(", ")}</span>}
                </div>
            }
        >
            {!editing && (
                <div className="cmp-field">
                    <label>File</label>
                    <div className="cmp-dropzone" onClick={() => fileRef.current?.click()}>
                        {file ? <><div className="cmp-dropzone-file">{file.name}</div><div className="cmp-hint">Click to choose a different file</div></> : "Click to choose a PDF, Word document or image"}
                    </div>
                    <input
                        type="file"
                        ref={fileRef}
                        style={{ display: "none" }}
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(e) => { if (e.target.files?.[0]) setFile(e.target.files[0]); e.target.value = ""; }}
                    />
                    <span className="cmp-hint">PDFs work best: officers read them in the app and every signed copy keeps the original pages.</span>
                </div>
            )}

            {!editing && types.length > 0 && (
                <div className="cmp-field">
                    <label>Document type</label>
                    <Select
                        allowClear
                        placeholder="e.g. SOP, Policy, Generic Risk Assessment"
                        className="custom-select"
                        value={documentConfigId}
                        onChange={chooseType}
                        options={types.map((t) => ({ value: t._id, label: t.name }))}
                    />
                </div>
            )}

            <div className="cmp-field">
                <label>Title</label>
                <Input className="generic-input-control" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Lone Working SOP" />
            </div>

            <div className="cmp-field">
                <label>Note for officers (optional)</label>
                <Input.TextArea className="generic-input-control" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown with the document in the app" />
            </div>

            <div className="cmp-field">
                <label>Who must acknowledge it</label>
                <button type="button" className="cmp-choice-card" onClick={() => setAudienceOpen(true)}>
                    <div className="cmp-choice-card-title"><FontAwesomeIcon icon={faUsers} style={{ marginRight: 6, color: "#00a4bd" }} />{summariseAudience(audience)}</div>
                    <div className="cmp-choice-card-text">Employees, sites, site groups, customers, branches or suppliers</div>
                </button>
            </div>

            <div className="cmp-field">
                <label>How officers confirm</label>
                <div className="cmp-choice">
                    <button type="button" className={`cmp-choice-card${method === "signature" ? " selected" : ""}`} onClick={() => setMethod("signature")}>
                        <div className="cmp-choice-card-title">Drawn signature</div>
                        <div className="cmp-choice-card-text">Officer signs on screen</div>
                    </button>
                    <button type="button" className={`cmp-choice-card${method === "tick" ? " selected" : ""}`} onClick={() => setMethod("tick")}>
                        <div className="cmp-choice-card-title">Tick box + typed name</div>
                        <div className="cmp-choice-card-text">Quicker, for notices and policy updates</div>
                    </button>
                </div>
            </div>

            <div style={{ display: "flex", gap: 16 }}>
                <div className="cmp-field" style={{ flex: 1 }}>
                    <label>Days to acknowledge</label>
                    <InputNumber min={0} max={365} precision={0} value={dueInDays} onChange={(v) => setDueInDays(v === null ? 0 : v)} className="generic-input-control" style={{ width: "100%" }} />
                    <span className="cmp-hint">0 = no due date</span>
                </div>
                <div className="cmp-field" style={{ flex: 1 }}>
                    <label>Review by</label>
                    <DatePicker className="generic-input-control" format="DD/MM/YYYY" value={reviewDate ? dayjs(reviewDate) : null} onChange={(v) => setReviewDate(v ? v.format("YYYY-MM-DD") : "")} style={{ width: "100%" }} />
                </div>
                {!editing && (
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Expires (optional)</label>
                        <DatePicker className="generic-input-control" format="DD/MM/YYYY" value={expiryDate ? dayjs(expiryDate) : null} onChange={(v) => setExpiryDate(v ? v.format("YYYY-MM-DD") : "")} style={{ width: "100%" }} />
                    </div>
                )}
            </div>

            {editing && <span className="cmp-hint">To change the file itself, use "New version" - everyone will be asked to sign the new version.</span>}

            <AudiencePickerModal visible={audienceOpen} value={audience} onClose={() => setAudienceOpen(false)} onChange={setAudience} />
        </Drawer>
    );
};
